import React, { useMemo, useState } from 'react';
import { LibraryItem } from '../../../../entities/LibraryItem';
import { LibraryItemType } from '../../../../entities/LibraryItemType';
import { useLibrary, useLibraryAlert } from '../../LibraryPage';

import { useIsMobile } from '../../../../components/Mui3WidthProvider';
import { FolderService } from '../../../../services/FolderService';
import { SermonSeriesService } from '../../../../services/SermonSeriesService';
import { BuilderAssetService } from '../../../../services/BuilderAssetService';
import { FileAssetService } from '../../../../services/FIleAssetService';
import { LinkAssetService } from '../../../../services/LinkAssetService';
import { convertLinkAssetToLibraryItem } from '../../../../entities/LinkAsset';
import { convertFileAssetToLibraryItem } from '../../../../entities/FileAsset';
import { convertBuilderAssetToLibraryItem } from '../../../../entities/BuilderAsset';
import { convertSeriesToLibraryItem } from '../../../../entities/SermonSeries';
import { convertFolderToLibraryItem } from '../../../../entities/Folder';
import { MobileMoveMenu } from './MobileMoveMenu';
import { MoveMenuFolderList } from './MoveMenuFolderList';
import { DesktopMoveMenu } from './DesktopMoveMenu';

interface Props {
    focusedItem: LibraryItem;
    open: boolean;
    onClose: () => void;
}

export function MoveAssetMenu(props:Props){
    const { focusedItem } = props
    const context = useLibrary();
    const libraryAlert = useLibraryAlert();
    
    const [selectedFolderId, setSelectedFolderId] = useState(focusedItem.parentId)
    const [disabled, setDisabled] = useState(false)
    const isMobile = useIsMobile();
    
    const parentFolder = useMemo(() => context.libraryItems.find(i => i.id === selectedFolderId), [selectedFolderId, context.libraryItems])

    const handleMove = async(newParentId:string | undefined) => {
        const newParent = context.libraryItems.find(i => i.id === newParentId);

        switch(focusedItem.type) {
			case LibraryItemType.Folder:
			case LibraryItemType.PremiumSermon:
			case LibraryItemType.PremiumSermonSeries:
				await handleFolderMove(newParent);
                break;
			case LibraryItemType.SermonSeries:
				await handleSeriesMove(newParent);
                break;
			case LibraryItemType.BuilderAsset:
				await handleAssetMove(newParent);
                break;
			case LibraryItemType.FileAsset:
				await handleFileMove(newParent);
                break;
			case LibraryItemType.LinkAsset:
				await handleLinkMove(newParent);
                break;
			default:
                break;
		}

        props.onClose()
    }

    const handleFolderMove = async(newParent:LibraryItem | undefined) => {
        setDisabled(true)
        const response = await FolderService.moveFolder(focusedItem.id, newParent ? newParent.id : null)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.updateLibraryItem(convertFolderToLibraryItem(response.data))
            libraryAlert(`${focusedItem.name} moved to ${newParent ? newParent.name : "Library Home"}`, async () => {
                const result = await FolderService.moveFolder(focusedItem.id, focusedItem.parentId ?? null);
                if(result.success) {
                    libraryAlert(`${focusedItem.name} moved`);
                } else {
                    libraryAlert(`Failed to move ${focusedItem.name}`);
                }
            });
        }
    }

    const handleSeriesMove = async(newParent:LibraryItem | undefined) => {
        setDisabled(true)
        const response = await SermonSeriesService.moveSeries(focusedItem.id, newParent ? newParent.id : null)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.updateLibraryItem(convertSeriesToLibraryItem(response.data))
            libraryAlert(`${focusedItem.name} moved to ${newParent ? newParent.name : "Library Home"}`, async () => {
                const result = await SermonSeriesService.moveSeries(focusedItem.id, focusedItem.parentId ?? null);
                if(result.success) {
                    libraryAlert(`${focusedItem.name} moved`);
                } else {
                    libraryAlert(`Failed to move ${focusedItem.name}`);
                }
            });
        }
    }

    const handleAssetMove = async(newParent:LibraryItem | undefined) => {
        setDisabled(true)
        const response = await BuilderAssetService.move(focusedItem.id, newParent ? newParent.id : null)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.updateLibraryItem(convertBuilderAssetToLibraryItem(response.data))
            libraryAlert(`${focusedItem.name} moved to ${newParent ? newParent.name : "Library Home"}`, async () => {
                const result = await BuilderAssetService.move(focusedItem.id, focusedItem.parentId ?? null);
                if(result.success) {
                    libraryAlert(`${focusedItem.name} moved`);
                } else {
                    libraryAlert(`Failed to move ${focusedItem.name}`);
                }
            });
        }
    }

    const handleFileMove = async(newParent:LibraryItem | undefined) => {
        setDisabled(true)
        const response = await FileAssetService.move(focusedItem.id, newParent ? newParent.id : null)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.updateLibraryItem(convertFileAssetToLibraryItem(response.data))
            libraryAlert(`${focusedItem.name} moved to ${newParent ? newParent.name : "Library Home"}`, async () => {
                const result = await FileAssetService.move(focusedItem.id, focusedItem.parentId ?? null);
                if(result.success) {
                    libraryAlert(`${focusedItem.name} moved`);
                } else {
                    libraryAlert(`Failed to move ${focusedItem.name}`);
                }
            });
        }
    }

    const handleLinkMove = async(newParent:LibraryItem | undefined) => {
        setDisabled(true)
        const response = await LinkAssetService.move(focusedItem.id, newParent ? newParent.id : null)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.updateLibraryItem(convertLinkAssetToLibraryItem(response.data))
            libraryAlert(`${focusedItem.name} moved to ${newParent ? newParent.name : "Library Home"}`, async () => {
                const result = await LinkAssetService.move(focusedItem.id, focusedItem.parentId ?? null);
                if(result.success) {
                    libraryAlert(`${focusedItem.name} moved`);
                } else {
                    libraryAlert(`Failed to move ${focusedItem.name}`);
                }
            });
        }
    }

    return (
            isMobile 
                ? 
                <MobileMoveMenu
                    disabled={disabled} 
                    open={props.open} 
                    onClose={props.onClose}
                    parentFolder={parentFolder}
                    handleBackClick={(id) => setSelectedFolderId(id)}
                >
                    <MoveMenuFolderList 
                        disabled={disabled}
                        handleClickIntoFolder={(id) => setSelectedFolderId(id)}
                        handleMove={handleMove}
                        itemToMove={focusedItem}
                        currentFolderId={selectedFolderId}                        
                    />
                </MobileMoveMenu> 
                : 
                <DesktopMoveMenu
                    disabled={disabled}
                    onClose={props.onClose}
                    parentFolder={parentFolder}
                    handleBackClick={(id) => setSelectedFolderId(id)}
                >
                    <MoveMenuFolderList
                        disabled={disabled}
                        handleClickIntoFolder={(id) => setSelectedFolderId(id)}
                        handleMove={handleMove}
                        itemToMove={focusedItem}
                        currentFolderId={selectedFolderId}                        
                    />
                </DesktopMoveMenu>
    )
}